import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Scissors, Eye, Heart } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function signIn() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const HERO_IMG = "https://images.unsplash.com/photo-1527203561188-dae1bc1a417f?w=1600&q=90";
const W1 = "https://images.unsplash.com/photo-1658497730270-b5f4fef00ae1?w=900&q=80";
const W2 = "https://images.unsplash.com/photo-1713845784497-fe3d7ed176d8?w=900&q=80";
const M1 = "https://images.pexels.com/photos/10930062/pexels-photo-10930062.jpeg?w=900&q=80";
const M2 = "https://images.pexels.com/photos/7447127/pexels-photo-7447127.jpeg?w=900&q=80";
const PREMIUM_IMG = "https://images.pexels.com/photos/7195803/pexels-photo-7195803.jpeg?w=1400&q=80";

export default function Landing() {
  return (
    <main data-testid="landing-page" className="text-bone">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-espresso via-espresso/70 to-transparent" />
        </div>
        <div className="relative crown-container pt-24 pb-32 md:pt-36 md:pb-44">
          <div className="max-w-2xl animate-fade-up">
            <p className="font-overline mb-6">Issue 001 · Spring/Summer Atelier</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-8 text-balance">
              The Crown<br/>
              <span className="italic text-gold">is yours.</span> We help<br/>
              you find its shape.
            </h1>
            <p className="text-lg text-ash max-w-lg mb-10 leading-relaxed">
              An AI atelier devoted to braids, fades, locs and natural beauty. Upload a single
              selfie — receive editorial recommendations, virtual try-ons and your stylist's
              blueprint, tailored to your face, hair and heritage.
            </p>
            <div className="flex flex-wrap gap-4">
              <button onClick={signIn} className="crown-btn-primary" data-testid="hero-cta-signin">
                Begin your reading <ArrowUpRight size={16} strokeWidth={1.5} />
              </button>
              <Link to="/gallery" className="crown-btn-ghost" data-testid="hero-cta-gallery">
                Browse the gallery
              </Link>
            </div>
            <div className="flex items-center gap-8 mt-14 text-sm">
              <div><p className="font-serif text-3xl text-gold">20+</p><p className="font-overline text-ash mt-1">Styles</p></div>
              <div className="w-px h-10 bg-bone/20" />
              <div><p className="font-serif text-3xl text-gold">6</p><p className="font-overline text-ash mt-1">Face shapes</p></div>
              <div className="w-px h-10 bg-bone/20" />
              <div><p className="font-serif text-3xl text-gold">4</p><p className="font-overline text-ash mt-1">Markets</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="crown-container py-28">
        <div className="grid lg:grid-cols-12 gap-12 items-end mb-16">
          <div className="lg:col-span-7">
            <p className="font-overline mb-4">The Ritual</p>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight text-balance">
              Three steps from selfie<br/>to <span className="italic text-gold">salon-ready</span>.
            </h2>
          </div>
          <p className="lg:col-span-5 text-ash leading-relaxed">
            We've codified the consultation a master braider gives a new client — analysis,
            recommendation, and the precise instructions to take to the chair.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-px bg-bone/10">
          {[
            { n: "01", t: "Read", d: "Computer vision identifies face shape, hair type, density and skin tone.", i: Eye },
            { n: "02", t: "Recommend", d: "AI curates styles matched to your structure, lifestyle and aesthetic.", i: Sparkles },
            { n: "03", t: "Realize", d: "Virtual try-on. Stylist instructions. Maintenance protocol.", i: Scissors },
          ].map((s, idx) => (
            <div key={s.n} className="bg-espresso p-10 hover:bg-obsidian transition-colors" data-testid={`step-${idx}`}>
              <div className="flex items-start justify-between mb-12">
                <p className="font-mono text-xs text-gold">{s.n}</p>
                <s.i className="text-gold" strokeWidth={1.5} size={20} />
              </div>
              <h3 className="font-serif text-3xl mb-3">{s.t}</h3>
              <p className="text-ash text-sm leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WOMEN GALLERY PREVIEW */}
      <section className="crown-container pb-24">
        <div className="grid lg:grid-cols-12 gap-8 mb-12">
          <div className="lg:col-span-5">
            <p className="font-overline mb-4">Women's Atelier</p>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight">
              Knotless. Fulani.<br/>Boho. <span className="italic text-gold">Yours.</span>
            </h2>
          </div>
          <p className="lg:col-span-5 lg:col-start-8 self-end text-ash">
            Box braids to lace fronts — every protective and natural style is documented with
            an installation guide and a maintenance protocol your stylist will recognize.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="row-span-2 col-span-2 aspect-[1/1] relative overflow-hidden group">
            <img src={W1} alt="Knotless braids" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-espresso/80 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <p className="font-overline text-gold">001</p>
              <p className="font-serif text-3xl mt-1">Knotless Braids</p>
            </div>
          </div>
          <div className="aspect-square relative overflow-hidden group">
            <img src={W2} alt="Natural afro" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-espresso/70 to-transparent" />
            <p className="absolute bottom-4 left-4 font-serif text-xl">Natural Afro</p>
          </div>
          <div className="aspect-square relative overflow-hidden group">
            <img src="https://images.unsplash.com/photo-1605497788044-5a32c7078486?w=900&q=80" alt="Fulani" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-espresso/70 to-transparent" />
            <p className="absolute bottom-4 left-4 font-serif text-xl">Fulani Braids</p>
          </div>
          <div className="aspect-square relative overflow-hidden group">
            <img src="https://images.unsplash.com/photo-1605980776566-0486c3ac7617?w=900&q=80" alt="Boho braids" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-espresso/70 to-transparent" />
            <p className="absolute bottom-4 left-4 font-serif text-xl">Boho Braids</p>
          </div>
          <div className="aspect-square relative overflow-hidden group">
            <img src="https://images.unsplash.com/photo-1620331311520-246422fd82f9?w=900&q=80" alt="Locs" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-espresso/70 to-transparent" />
            <p className="absolute bottom-4 left-4 font-serif text-xl">Locs</p>
          </div>
        </div>
      </section>

      {/* MEN SECTION */}
      <section className="bg-obsidian py-28">
        <div className="crown-container">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5 lg:col-start-1 space-y-6">
              <p className="font-overline">Men's Atelier</p>
              <h2 className="font-serif text-4xl sm:text-5xl leading-tight">
                Fades that frame.<br/><span className="italic text-gold">Cuts that command.</span>
              </h2>
              <p className="text-ash leading-relaxed">
                From the low fade boardroom standard to the burst-faded mohawk. We translate
                your bone structure into the cut, taper and line-up that earns the chair.
              </p>
              <Link to="/gallery?gender=men" className="crown-btn-ghost inline-flex" data-testid="men-cta">
                See men's styles <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 grid grid-cols-2 gap-3">
              <img src={M1} alt="" className="aspect-[3/4] object-cover" />
              <img src={M2} alt="" className="aspect-[3/4] object-cover mt-10" />
            </div>
          </div>
        </div>
      </section>

      {/* PREMIUM */}
      <section className="crown-container py-28">
        <div className="relative overflow-hidden">
          <img src={PREMIUM_IMG} alt="" className="w-full h-[60vh] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-tr from-espresso via-espresso/60 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="crown-container">
              <div className="max-w-xl">
                <p className="font-overline mb-4">Premium · $9.99/mo</p>
                <h2 className="font-serif text-4xl sm:text-5xl leading-tight mb-6 text-balance">
                  An atelier-level membership<br/>
                  for the <span className="italic text-gold">style-obsessed</span>.
                </h2>
                <ul className="space-y-3 mb-8 text-ash">
                  {[
                    "Unlimited AI selfie analyses",
                    "Photoreal virtual try-on with Nano Banana",
                    "Stylist & barber installation guides",
                    "Save favorites · grooming protocols · country trend feeds",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <Heart size={14} className="text-gold mt-1.5" strokeWidth={1.5} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/pricing" className="crown-btn-primary" data-testid="landing-pricing-cta">
                  Become a member <ArrowUpRight size={16} strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-bone/10 py-12">
        <div className="crown-container flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <p className="font-serif text-2xl">Style Me Perfectly!</p>
            <p className="font-overline text-ash mt-2">© {new Date().getFullYear()} · An atelier of one</p>
          </div>
          <div className="flex flex-wrap gap-8">
            <Link to="/gallery" className="font-overline text-ash hover:text-gold transition-colors">Gallery</Link>
            <Link to="/pricing" className="font-overline text-ash hover:text-gold transition-colors">Premium</Link>
          </div>
        </div>
      </footer>
    </main>
  );
}
